import { useEffect, useState } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import Table from '../components/Table';
import StatusBadge from '../components/StatusBadge';
import LookupSelect, { LookupOption } from '../components/LookupSelect';

const fetchEmployeeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/employees');
  const employees = res.data.employees || [];
  const filtered = search ? employees.filter((e: any) => e.full_name.toLowerCase().includes(search.toLowerCase())) : employees;
  return filtered.slice(0, 20).map((e: any) => ({ value: e.id, label: `${e.full_name} (${e.code})` }));
};

export default function OffInLieu() {
  const [credits, setCredits] = useState<any[]>([]);
  const [mode, setMode] = useState<'INDIVIDUAL' | 'BATCH'>('INDIVIDUAL');
  const [employeeId, setEmployeeId] = useState('');
  const [batchEmployeeIds, setBatchEmployeeIds] = useState<string[]>([]);
  const [batchEmployeeNames, setBatchEmployeeNames] = useState<Record<string, string>>({});
  const [form, setForm] = useState({ creditType: 'DAILY', quantity: 1, creditDate: '', expiryDate: '', remarks: '' });

  const load = async () => {
    const res = await api.get('/lms/oil-credits');
    setCredits(res.data.credits || []);
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    try {
      if (mode === 'INDIVIDUAL') {
        if (!employeeId) return alert('Select an employee');
        await api.post('/lms/oil-credits', { employeeId, ...form });
      } else {
        if (!batchEmployeeIds.length) return alert('Add at least one employee');
        await api.post('/lms/oil-credits/batch', { employeeIds: batchEmployeeIds, ...form });
      }
      setForm({ creditType: 'DAILY', quantity: 1, creditDate: '', expiryDate: '', remarks: '' });
      setEmployeeId('');
      setBatchEmployeeIds([]);
      load();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to credit');
    }
  };

  const runExpiry = async () => {
    const res = await api.post('/lms/oil-credits/expire');
    alert(`${res.data.expired} credit(s) marked expired`);
    load();
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Off-in-Lieu — Transaction / Batch Leave</h1>
        <Button variant="secondary" onClick={runExpiry}>Run Expiry Check</Button>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-6 space-y-4">
        <div className="flex gap-4">
          <button onClick={() => setMode('INDIVIDUAL')} className={`px-3 py-1 rounded text-sm ${mode === 'INDIVIDUAL' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>Individual</button>
          <button onClick={() => setMode('BATCH')} className={`px-3 py-1 rounded text-sm ${mode === 'BATCH' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>Batch</button>
        </div>

        {mode === 'INDIVIDUAL' ? (
          <LookupSelect label="Employee" value={employeeId} onChange={setEmployeeId} fetchOptions={fetchEmployeeOptions} required />
        ) : (
          <div>
            <label className="text-sm font-medium text-gray-700">Employees</label>
            <LookupSelect
              value=""
              placeholder="Add employee..."
              fetchOptions={fetchEmployeeOptions}
              onChange={(v, opt) => {
                if (!v || batchEmployeeIds.includes(v)) return;
                setBatchEmployeeIds([...batchEmployeeIds, v]);
                if (opt) setBatchEmployeeNames({ ...batchEmployeeNames, [v]: opt.label });
              }}
            />
            <div className="flex flex-wrap gap-2 mt-2">
              {batchEmployeeIds.map((id) => (
                <span key={id} className="bg-gray-100 rounded-full px-3 py-1 text-xs flex items-center gap-1">
                  {batchEmployeeNames[id] || id}
                  <button onClick={() => setBatchEmployeeIds(batchEmployeeIds.filter((e) => e !== id))} title="Remove">&times;</button>
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-4">
          <Select
            label="Credit Type"
            value={form.creditType}
            onChange={(e) => setForm({ ...form, creditType: e.target.value })}
            options={[{ value: 'DAILY', label: 'Daily' }, { value: 'HOURLY', label: 'Hourly' }]}
          />
          <Input label="Quantity" type="number" step="0.5" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) })} />
          <Input label="Credit Date" type="date" value={form.creditDate} onChange={(e) => setForm({ ...form, creditDate: e.target.value })} />
          <Input label="Expiry Date (forfeit after)" type="date" value={form.expiryDate} onChange={(e) => setForm({ ...form, expiryDate: e.target.value })} />
          <Input label="Remarks" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="col-span-2" />
        </div>
        <Button onClick={submit}>Credit Off-in-Lieu</Button>
      </div>

      <Table
        columns={[
          { key: 'employee', header: 'Employee', render: (r: any) => `${r.first_name} ${r.last_name} (${r.employee_code})` },
          { key: 'credit_type', header: 'Type' },
          { key: 'quantity', header: 'Credited' },
          { key: 'quantity_remaining', header: 'Remaining' },
          { key: 'credit_date', header: 'Credit Date', render: (r: any) => new Date(r.credit_date).toLocaleDateString() },
          { key: 'expiry_date', header: 'Expiry', render: (r: any) => (r.expiry_date ? new Date(r.expiry_date).toLocaleDateString() : '—') },
          { key: 'status', header: 'Status', render: (r: any) => <StatusBadge status={r.status} /> },
        ]}
        rows={credits}
      />
    </div>
  );
}
